'use client';

import { useState } from 'react';

import { ChevronDown } from 'lucide-react';

/**
 * FAQ accordion with smooth open/close animation. Native <details> can't
 * animate (the UA hides closed content with display:none), so this uses the
 * grid-template-rows 0fr→1fr transition — height animates smoothly to the
 * answer's natural size. Answers stay in the DOM for SEO.
 *
 * Question/answer content arrives as ALREADY-RENDERED nodes from the server
 * parent (landers namespace resolves at prerender) — this component holds no
 * i18n dependency, which keeps the 180KB landers copy out of the client
 * bundle entirely.
 */
export function LandingFaqAccordion({
  items,
}: {
  items: Array<{
    id: number;
    question: React.ReactNode;
    answer: React.ReactNode;
  }>;
}) {
  const [openId, setOpenId] = useState<number | null>(null);

  return (
    <div className="flex flex-col" style={{ marginTop: 50, gap: 16 }}>
      {items.map((item) => {
        const isOpen = openId === item.id;

        return (
          <div
            key={item.id}
            style={{
              // Solid dark fill (not translucent) so the purple page glow
              // doesn't tint the rows — matches the reference's card colour.
              background: '#15101d',
              border: `1px solid ${isOpen ? 'rgba(255,158,210,0.45)' : 'rgba(255,255,255,0.14)'}`,
              borderRadius: 14,
              transition: 'border-color 0.25s ease',
            }}
          >
            <button
              type="button"
              onClick={() => setOpenId(isOpen ? null : item.id)}
              aria-expanded={isOpen}
              className="flex w-full cursor-pointer items-center justify-between text-left font-normal text-white"
              style={{
                padding: '16px 18px 16px 26px',
                gap: 16,
                fontSize: 16,
                lineHeight: 1.4,
              }}
            >
              {item.question}
              <span
                className="flex shrink-0 items-center justify-center rounded-full"
                style={{
                  width: 32,
                  height: 32,
                  transition: 'background-color 0.25s ease',
                  backgroundColor: isOpen
                    ? 'rgba(255,255,255,0.12)'
                    : 'rgba(255,255,255,0.05)',
                }}
              >
                <ChevronDown
                  className="h-4 w-4 text-white"
                  style={{
                    transition: 'transform 0.3s ease',
                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
              </span>
            </button>

            <div
              style={{
                display: 'grid',
                gridTemplateRows: isOpen ? '1fr' : '0fr',
                transition: 'grid-template-rows 0.35s ease',
              }}
            >
              <div className="overflow-hidden">
                <p
                  className="text-white/60"
                  style={{
                    padding: '0 26px 16px',
                    fontSize: 15,
                    lineHeight: 1.55,
                  }}
                >
                  {item.answer}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
